import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { cardLinkProps } from "@/lib/card-link";
import { OFFICIAL_CARD_BY_CODE, normalizeCardCode } from "@/lib/tcg-cards";

interface SiloGatewayProps {
  /** Which silo this gateway belongs to. */
  mode: "game" | "tcg";
  /** The Pal or card name being linked. */
  name: string;
  /** Slug for the Pal page (game mode) or card code (tcg mode). */
  target: string;
  className?: string;
}

/**
 * Bidirectional silo gateway block.
 *
 * - game mode: placed on a Pal page and links to the TCG card detail page.
 * - tcg mode: placed on a card page and links back to the Pal detail page.
 *
 * Anchor text is deliberately structural ("Palworld TCG Card List & Price
 * Trends", "Palworld Pal Database") so LLM crawlers learn the entity split
 * between the video-game hub and the trading-card financial hub.
 */
export function SiloGateway({ mode, name, target, className }: SiloGatewayProps) {
  if (mode === "game") {
    // Only link to a card detail page when the code actually exists in the
    // card database — internal game data references (ECG-*, ECE-*) are not
    // printed English card numbers and would land on a dead page.
    const exists = OFFICIAL_CARD_BY_CODE.has(normalizeCardCode(target));
    return (
      <div className={`rounded-xl border border-primary/20 bg-primary/5 p-4 ${className ?? ""}`}>
        <h3 className="text-sm font-semibold text-primary">Palworld TCG crossover</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {name} also appears as an official Palworld OFFICIAL CARD GAME printing.
        </p>
        {exists ? (
          <Link
            {...cardLinkProps(target)}
            className="mt-2 inline-block text-sm font-semibold text-primary hover:underline"
          >
            View the {name} Palworld TCG Card List &amp; Price Trends →
          </Link>
        ) : (
          <Link
            to="/cards"
            className="mt-2 inline-block text-sm font-semibold text-primary hover:underline"
          >
            Browse the Palworld TCG Card List &amp; Price Trends →
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 ${className ?? ""}`}>
      <h3 className="text-sm font-semibold text-emerald-400">Palworld video game crossover</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {name} is also a Pal in the Palworld video game.
      </p>
      <Link
        to="/pals/$slug"
        params={{ slug: target }}
        className="mt-2 inline-block text-sm font-semibold text-emerald-400 hover:underline"
      >
        View the {name} Palworld Pal Database — stats, spawns &amp; breeding →
      </Link>
    </div>
  );
}

interface AnchorLinkProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  children: ReactNode;
  className?: string;
}

/** High-value structural anchor wrapper used inside link lists. */
export function AnchorLink({ to, params, search, children, className }: AnchorLinkProps) {
  return (
    <Link
      to={to as any}
      params={params as any}
      search={search as any}
      className={`text-primary hover:underline ${className ?? ""}`}
    >
      {children}
    </Link>
  );
}
